import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Music2, Sparkles, Trash2, Upload, VolumeX, Wand2 } from 'lucide-react';
import { Button } from '../ui/button';
import { absUrl, autoScorePreviewUrl, composeAiScore, errMsg, removeMusic, uploadMusic } from '../../lib/aiStudioApi';
import { cn } from '../../lib/utils';

const AI_SCORE_CREDITS = 24;
const OPTIONS = [
  ['auto', 'Auto score', Wand2, 'Free'],
  ['track', 'AI score or your track', Sparkles, 'Optional'],
  ['off', 'No music', VolumeX, ''],
];

function TrackPanel({ project, busy, run }) {
  const ref = useRef(null);
  const m = project.music;
  const ai = project.music_ai || {};
  const composing = ai.status === 'running';
  const compose = () => {
    if (!window.confirm(`Compose an AI score for this production? About ${AI_SCORE_CREDITS} credits, once.`)) return;
    run(() => composeAiScore(project.id), 'Composing your AI score — about 1 to 3 minutes');
  };
  return (
    <div className="space-y-3">
      {m && (
        <div className="flex flex-wrap items-center gap-3" data-testid="music-track">
          <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] text-amber-200">{m.source === 'ai' ? 'AI score' : 'Uploaded'}</span>
          <span className="max-w-[240px] truncate text-xs text-neutral-300" data-testid="music-name">{m.name}</span>
          <audio src={absUrl(m.url)} controls preload="none" className="h-8" />
          <Button size="sm" variant="ghost" disabled={busy} onClick={() => run(() => removeMusic(project.id), 'Back to the free auto score')}
            className="text-neutral-400 hover:text-red-300" data-testid="music-remove-btn"><Trash2 size={13} /></Button>
        </div>
      )}
      <input ref={ref} type="file" accept=".mp3,.wav,.m4a,.aac,.ogg,audio/*" className="hidden" data-testid="music-file-input"
        onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) run(() => uploadMusic(project.id, f), 'Music added'); }} />
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" disabled={busy || composing} onClick={compose} className="bg-amber-500 text-neutral-900 hover:bg-amber-400" data-testid="music-ai-compose-btn">
          {composing ? <Loader2 size={13} className="mr-1 animate-spin" /> : <Sparkles size={13} className="mr-1" />}
          {composing ? 'Composing AI score…' : `${m?.source === 'ai' ? 'Recompose' : 'Compose'} AI score · ${AI_SCORE_CREDITS} cr`}
        </Button>
        <Button size="sm" variant="outline" disabled={busy} onClick={() => ref.current?.click()} className="border-neutral-700 text-neutral-200" data-testid="music-upload-btn">
          <Upload size={13} className="mr-1" /> Upload your own (optional)
        </Button>
      </div>
      {ai.status === 'failed' && <div className="text-[11px] text-red-300" data-testid="music-ai-error">AI score failed: {ai.error || 'please retry'} — the free auto score is used meanwhile.</div>}
    </div>
  );
}

/** Music bed — automatic by default: a free score matched to the tone, blended across the whole movie and ducked under dialogue. */
export function MusicBed({ project, onPatch, onChanged }) {
  const [busy, setBusy] = useState(false);
  const saved = project.music_mode || (project.music ? 'track' : 'auto');
  const [view, setView] = useState(saved);
  useEffect(() => { setView(saved); }, [saved]);
  const composing = (project.music_ai || {}).status === 'running';
  useEffect(() => {
    if (!composing) return undefined;
    const t = setInterval(onChanged, 6000);
    return () => clearInterval(t);
  }, [composing, onChanged]);
  const run = async (fn, ok) => { setBusy(true); try { await fn(); toast.success(ok); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); onChanged(); } };
  const pick = (v) => { setView(v); if (v !== 'track' || project.music) onPatch({ music_mode: v }); };
  const tone = project.tone || 'cinematic';
  return (
    <div className="space-y-3 rounded-xl border border-neutral-800 bg-neutral-900/40 p-4" data-testid="music-bed">
      <div className="flex flex-wrap items-center gap-2 text-sm font-semibold text-neutral-100">
        <Music2 size={15} className="text-amber-300" /> Music bed
        <span className="text-[11px] font-normal text-neutral-500">· automatic · blended across the whole movie · ducked under every line</span>
      </div>
      <div className="grid gap-2 sm:grid-cols-3" data-testid="music-mode-switch">
        {OPTIONS.map(([v, title, Icon, tag]) => (
          <button key={v} type="button" onClick={() => pick(v)} data-testid={`music-mode-${v}`}
            className={cn('flex items-center gap-2 rounded-lg border px-3 py-2 text-left text-xs transition-colors duration-200',
              view === v ? 'border-amber-400 bg-amber-500/10 text-neutral-100' : 'border-neutral-800 text-neutral-400 hover:border-neutral-600')}>
            <Icon size={14} className="text-amber-300" /> <span className="font-medium">{title}</span>
            {tag && <span className="ml-auto text-[10px] text-neutral-500">{tag}</span>}
          </button>
        ))}
      </div>
      {view === 'auto' && (
        <div className="flex flex-wrap items-center gap-3 text-xs text-neutral-400" data-testid="music-auto-panel">
          <span>Composed for the <strong className="text-neutral-200">{tone}</strong> tone, at the exact length of your movie — fades in and out, no loop seams. 0 credits.</span>
          <audio src={autoScorePreviewUrl(tone)} controls preload="none" className="h-8" data-testid="music-auto-preview" />
        </div>
      )}
      {view === 'track' && <TrackPanel project={project} busy={busy} run={run} />}
      {view === 'off' && <div className="text-xs text-neutral-500" data-testid="music-off-note">No music bed — the Final Cut keeps dialogue and in-clip sound only.</div>}
    </div>
  );
}
